import { useMemo } from "react";

export default function OperationsViewSummary({
  locale,
  scopedWorkspaceMetrics,
  procurementQueueRows,
  qualityQueueRows,
  logisticsQueueRows
}) {
  const procurementReadyRows = useMemo(
    () => procurementQueueRows.filter((item) => item.procurementStatus === "ready_to_purchase"),
    [procurementQueueRows]
  );

  const procurementCostingRows = useMemo(
    () => procurementQueueRows.filter((item) => item.procurementStatus !== "ready_to_purchase"),
    [procurementQueueRows]
  );

  const procurementPriorityRows = useMemo(
    () => procurementQueueRows
      .slice()
      .sort((left, right) => {
        const leftScore =
          (left.procurementStatus === "ready_to_purchase" ? 45 : 0) +
          Number(left.grossMarginRate || 0) -
          Number(left.supplierLeadDays || 0) / 2;
        const rightScore =
          (right.procurementStatus === "ready_to_purchase" ? 45 : 0) +
          Number(right.grossMarginRate || 0) -
          Number(right.supplierLeadDays || 0) / 2;
        return rightScore - leftScore;
      })
      .slice(0, 6),
    [procurementQueueRows]
  );

  const qualityInspectionRows = useMemo(
    () => qualityQueueRows.filter((item) => item.qualityStatus === "inspection_pending"),
    [qualityQueueRows]
  );

  const qualityBlockedRows = useMemo(
    () => qualityQueueRows.filter((item) => item.qualityStatus !== "inspection_pending"),
    [qualityQueueRows]
  );

  const qualityPriorityRows = useMemo(
    () => qualityQueueRows
      .slice()
      .sort((left, right) => {
        const leftScore =
          (left.qualityStatus === "inspection_pending" ? 40 : 0) +
          (left.sampleStatus === "sample_pending" ? 15 : 0) +
          (left.complianceStatus === "docs_pending" ? 10 : 0);
        const rightScore =
          (right.qualityStatus === "inspection_pending" ? 40 : 0) +
          (right.sampleStatus === "sample_pending" ? 15 : 0) +
          (right.complianceStatus === "docs_pending" ? 10 : 0);
        return rightScore - leftScore;
      })
      .slice(0, 6),
    [qualityQueueRows]
  );

  const logisticsInTransitRows = useMemo(
    () => logisticsQueueRows.filter((item) => item.status === "in_transit"),
    [logisticsQueueRows]
  );

  const logisticsPreShipmentRows = useMemo(
    () => logisticsQueueRows.filter((item) => ["paid", "packed"].includes(String(item.status || ""))),
    [logisticsQueueRows]
  );

  const logisticsDeliveredRows = useMemo(
    () => logisticsQueueRows.filter((item) => item.status === "delivered"),
    [logisticsQueueRows]
  );

  const logisticsPriorityRows = useMemo(
    () => logisticsQueueRows
      .slice()
      .sort((left, right) => {
        const priority = {
          paid: 4,
          packed: 3,
          in_transit: 2,
          delivered: 1,
          cancelled: 0
        };
        return (priority[String(right.status || "")] || 0) - (priority[String(left.status || "")] || 0);
      })
      .slice(0, 6),
    [logisticsQueueRows]
  );

  const procurementHeroMetrics = useMemo(() => ([
    {
      key: "queue",
      label: locale === "zh" ? "待推进采购" : "Open Queue",
      value: procurementQueueRows.length,
      note: locale === "zh" ? "进入采购视角的商品数" : "Products currently in sourcing review"
    },
    {
      key: "ready",
      label: locale === "zh" ? "可下单" : "Ready to Order",
      value: procurementQueueRows.filter((item) => item.procurementStatus === "ready_to_purchase").length,
      note: locale === "zh" ? "可直接转采购动作" : "Can move directly into purchasing"
    },
    {
      key: "costing",
      label: locale === "zh" ? "待核价" : "Pending Costing",
      value: procurementQueueRows.filter((item) => item.procurementStatus !== "ready_to_purchase").length,
      note: locale === "zh" ? "仍需补成本或报价" : "Still missing costing or quotation confirmation"
    },
    {
      key: "lead",
      label: locale === "zh" ? "平均交期" : "Avg Lead Time",
      value: procurementQueueRows.length
        ? `${Math.round(procurementQueueRows.reduce((sum, item) => sum + Number(item.supplierLeadDays || 0), 0) / procurementQueueRows.length)}d`
        : "0d",
      note: locale === "zh" ? "供应侧节奏参考" : "Current supply-side pace"
    }
  ]), [locale, procurementQueueRows]);

  const qualityHeroMetrics = useMemo(() => ([
    {
      key: "queue",
      label: locale === "zh" ? "待质检" : "Open QA Queue",
      value: qualityQueueRows.length,
      note: locale === "zh" ? "需要进入放行判断的商品" : "Products needing QA release decisions"
    },
    {
      key: "inspection",
      label: locale === "zh" ? "待验货" : "Inspection Pending",
      value: qualityQueueRows.filter((item) => item.qualityStatus === "inspection_pending").length,
      note: locale === "zh" ? "样品或出货前验货待排期" : "Sample or pre-shipment checks to schedule"
    },
    {
      key: "blocked",
      label: locale === "zh" ? "前置阻塞" : "Upstream Blockers",
      value: qualityQueueRows.filter((item) => item.qualityStatus !== "inspection_pending").length,
      note: locale === "zh" ? "多为资料未齐或选品未完成" : "Usually blocked by incomplete merchandising inputs"
    },
    {
      key: "samples",
      label: locale === "zh" ? "样品待确认" : "Samples Pending",
      value: scopedWorkspaceMetrics.samplePendingCount || 0,
      note: locale === "zh" ? "会直接影响质检排期" : "Directly impacts QA scheduling"
    }
  ]), [locale, qualityQueueRows, scopedWorkspaceMetrics.samplePendingCount]);

  const logisticsHeroMetrics = useMemo(() => ([
    {
      key: "shipments",
      label: locale === "zh" ? "发运单量" : "Shipment Count",
      value: logisticsQueueRows.length,
      note: locale === "zh" ? "纳入物流协同的订单" : "Orders currently in logistics view"
    },
    {
      key: "transit",
      label: locale === "zh" ? "运输中" : "In Transit",
      value: logisticsQueueRows.filter((item) => item.status === "in_transit").length,
      note: locale === "zh" ? "需要重点跟踪时效" : "Requires active ETA monitoring"
    },
    {
      key: "pending",
      label: locale === "zh" ? "待发运" : "Pre-Shipment",
      value: logisticsQueueRows.filter((item) => ["paid", "packed"].includes(String(item.status || ""))).length,
      note: locale === "zh" ? "仍在备货或等待交运" : "Still being packed or awaiting handoff"
    },
    {
      key: "delivered",
      label: locale === "zh" ? "已完成" : "Delivered",
      value: logisticsQueueRows.filter((item) => item.status === "delivered").length,
      note: locale === "zh" ? "已完成履约闭环" : "Fulfillment loop already closed"
    }
  ]), [locale, logisticsQueueRows]);

  const procurementViewTabs = useMemo(() => ([
    {
      key: "overview",
      label: locale === "zh" ? "总览" : "Overview",
      count: procurementPriorityRows.length
    },
    {
      key: "costing",
      label: locale === "zh" ? "待核价" : "Pending Costing",
      count: procurementCostingRows.length
    },
    {
      key: "ready",
      label: locale === "zh" ? "可下单" : "Ready to Order",
      count: procurementReadyRows.length
    }
  ]), [locale, procurementCostingRows.length, procurementPriorityRows.length, procurementReadyRows.length]);

  const qualityViewTabs = useMemo(() => ([
    {
      key: "overview",
      label: locale === "zh" ? "总览" : "Overview",
      count: qualityPriorityRows.length
    },
    {
      key: "inspection",
      label: locale === "zh" ? "待验货" : "Inspection",
      count: qualityInspectionRows.length
    },
    {
      key: "blockers",
      label: locale === "zh" ? "前置阻塞" : "Blockers",
      count: qualityBlockedRows.length
    }
  ]), [locale, qualityBlockedRows.length, qualityInspectionRows.length, qualityPriorityRows.length]);

  const logisticsViewTabs = useMemo(() => ([
    {
      key: "overview",
      label: locale === "zh" ? "总览" : "Overview",
      count: logisticsPriorityRows.length
    },
    {
      key: "preShipment",
      label: locale === "zh" ? "待发运" : "Pre-Shipment",
      count: logisticsPreShipmentRows.length
    },
    {
      key: "transit",
      label: locale === "zh" ? "运输中" : "In Transit",
      count: logisticsInTransitRows.length
    },
    {
      key: "delivered",
      label: locale === "zh" ? "已完成" : "Delivered",
      count: logisticsDeliveredRows.length
    }
  ]), [locale, logisticsDeliveredRows.length, logisticsInTransitRows.length, logisticsPreShipmentRows.length, logisticsPriorityRows.length]);

  return {
    procurementReadyRows,
    procurementCostingRows,
    procurementPriorityRows,
    qualityInspectionRows,
    qualityBlockedRows,
    qualityPriorityRows,
    logisticsInTransitRows,
    logisticsPreShipmentRows,
    logisticsDeliveredRows,
    logisticsPriorityRows,
    procurementHeroMetrics,
    qualityHeroMetrics,
    logisticsHeroMetrics,
    procurementViewTabs,
    qualityViewTabs,
    logisticsViewTabs
  };
}
